import type { Sort } from '@/stores/sorting';
import type { CustomerRequest, CustomerResponse, DataResponse, InvoiceLineTaxType } from '@/types';
import type { VIESCheckResponse } from '@/types/invoice.ts';
import { sortToQuery } from '@/utils/misc.ts';
import type { AxiosInstance, AxiosResponse } from 'axios';

class CustomerService {
    private readonly apiService;

    constructor(apiService: AxiosInstance) {
        this.apiService = apiService;
    }

    getPath(id?: string) {
        return id ? `/api/customers/${id}` : '/api/customers';
    }

    index(page: number, filter?: object, sort: null | Sort = null, perPage?: number) {
        return this.apiService.get(this.getPath(), {
            params: { page: page, per_page: perPage, ...filter, ...sortToQuery(sort) },
        });
    }

    show(id: string) {
        return this.apiService.get(this.getPath(id));
    }

    store(request: CustomerRequest) {
        return this.apiService.post(this.getPath(), request);
    }

    update(id: string, request: CustomerRequest): Promise<AxiosResponse<CustomerResponse>> {
        return this.apiService.put(this.getPath(id), request);
    }

    delete(id: string) {
        return this.apiService.delete(this.getPath(id));
    }

    populators() {
        return this.apiService.get(`${this.getPath()}/populators`);
    }

    /** The VAT code Milo proposes for a new line to this customer; `tax` is null when none is on offer. */
    defaultTax(id: string): Promise<AxiosResponse<DataResponse<{ tax: InvoiceLineTaxType | null }>>> {
        return this.apiService.get(`${this.getPath(id)}/default-tax`);
    }

    copyBulk(customerIds: string[], targetCompanyId: string) {
        return this.apiService.post('/api/customers/bulk/copy', {
            customer_ids: customerIds,
            target_company_id: targetCompanyId,
        });
    }

    validateVATNumber(vatNumber: string): Promise<AxiosResponse<{ data: VIESCheckResponse }>> {
        return this.apiService.get('/api/vat/validate', {
            params: { id: vatNumber },
        });
    }
}

export default CustomerService;
